import { useCallback, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Modal from "react-bootstrap/Modal";
import {
  FaArrowDown,
  FaBan,
  FaBars,
  FaCheckDouble,
  FaClock,
  FaCog,
  FaDownload,
  FaEllipsisV,
  FaEnvelope,
  FaHistory,
  FaInfoCircle,
  FaPaperPlane,
  FaSearch,
  FaSync,
  FaTimes,
  FaTrash,
  FaTrashAlt,
  FaUserFriends,
  FaUsersSlash,
} from "react-icons/fa";
import {
  clearChatState,
  disconnectSocket,
  getAuthUser,
  initSocketConnection,
  loadChatState,
  saveChatState,
} from "../../services/chatService";
import {
  ConversationSkeleton,
  MessagesSkeleton,
  UsersSkeleton,
} from "./ChatSkeletons";
import GroupConversations from "./GroupConversations";
import GroupMembersOverlay from "./GroupMembersOverlay";
import "./MessagingModule.scss";
import Tabs from "./Tabs";

import { IoChatbubbleEllipsesSharp } from "react-icons/io5";

// Import API services
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  blockUser,
  clearConversationHistory,
  clearGroupHistory,
  createGroupChat,
  deleteAllDirectMessages,
  deleteAllGroupMessages,
  deleteConversationForUser,
  deleteGroup,
  deleteMessage,
  deleteMultipleMessages,
  getAllGroupMessages,
  getBlockedUsers,
  getDirectConversations,
  getDirectMessages,
  getGroupMessages,
  getUnreadCounts,
  getUserGroups,
  getUsersByRole,
  getUsersForChat,
  leaveGroup,
  markAllDirectMessagesRead,
  markAllMessagesAsRead,
  removeGroupMember,
  sendDirectMessage,
  sendGroupMessage,
  startDirectChat,
  unblockUser,
  updateGroup,
} from "../../services/chatService";
import ChatUserLisetSelector from "../selectbox/ChatUserLisetSelector";
import { server } from "../../App";

const MessagingModule = () => {
  const { t } = useTranslation();
  // Load persisted state (only UI state, not chat data)
  const persistedState = loadChatState();

  // FIX 1: Get initial state properly
  const initialActiveTab = persistedState.activeTab || "direct";
  const initialActiveConversation = persistedState.activeConversation || null;
  const initialChatType = persistedState.chatType || "direct";

  // State Management
  const [activeTab, setActiveTab] = useState(initialActiveTab);
  const [message, setMessage] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [activeConversation, setActiveConversation] = useState(
    initialActiveConversation,
  );
  const [currentChatType, setCurrentChatType] = useState(initialChatType);

  // Modal States
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupMembers, setGroupMembers] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveGroupId, setLeaveGroupId] = useState(null);
  const [isLeaveLoading, setIsLeaveLoading] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);
  const [editGroupData, setEditGroupData] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editGroupLoading, setEditGroupLoading] = useState(false);
  const [editGroupError, setEditGroupError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState(null);

  // Message Management Modals
  const [showClearChatModal, setShowClearChatModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [showMarkAllReadModal, setShowMarkAllReadModal] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [showSelectMessages, setShowSelectMessages] = useState(false);

  // Block Management
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockLoading, setBlockLoading] = useState(false);
  const [unBlockLoading, setUnBlockLoading] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);
  const [blockedUsers, setBlockedUsers] = useState([]);

  // Message Actions
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageActions, setShowMessageActions] = useState(false);
  const [messageActionPosition, setMessageActionPosition] = useState({
    x: 0,
    y: 0,
  });
  const [deleteForEveryone, setDeleteForEveryone] = useState(false);
  const [showDeleteMessageModal, setShowDeleteMessageModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  // Online users — separate from conversations to avoid race conditions on refetch
  const [onlineUserIds, setOnlineUserIds] = useState(new Set());

  // Users who have blocked the current user (separate from blockedUsers which is who I blocked)
  const [blockedByUserIds, setBlockedByUserIds] = useState(new Set());

  // Data States
  const [directConversations, setDirectConversations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [filteredAvailableUsers, setFilteredAvailableUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState({
    conversations: false,
    messages: false,
    users: false,
    groups: false,
  });
  const [error, setError] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({
    directUnread: 0,
    groupUnread: 0,
    totalUnread: 0,
  });

  // Socket & UI States
  const [socket, setSocket] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [lastTypingTime, setLastTypingTime] = useState(0);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessageUpdate, setLastMessageUpdate] = useState(Date.now());
  const [actionLoading, setActionLoading] = useState(false);
  const [hasLoadedMessages, setHasLoadedMessages] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // FIX 2: Add a flag to track initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const activeConversationRef = useRef(activeConversation);
  const currentChatTypeRef = useRef(currentChatType);
  const messagesRef = useRef(messages);
  const authUserIdRef = useRef(authUser?.id);
  const socketRef = useRef(null);
  const isConnectedRef = useRef(isConnected);
  const groupsRef = useRef(groups);
  const isLoadingRef = useRef(false);

  // Update refs when state changes
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    currentChatTypeRef.current = currentChatType;
  }, [currentChatType]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    authUserIdRef.current = authUser?.id;
  }, [authUser?.id]);

  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    isLoadingRef.current = loading.messages;
  }, [loading.messages]);

  // Save UI state to sessionStorage when it changes
  useEffect(() => {
    saveChatState(activeConversation, currentChatType, activeTab);
  }, [activeConversation, currentChatType, activeTab]);

  // Get auth user
  useEffect(() => {
    const user = getAuthUser();
    setAuthUser(user);
    authUserIdRef.current = user?.id;
  }, []);

  // Initialize Socket.IO
  useEffect(() => {
    const initializeSocket = () => {
      const newSocket = initSocketConnection();
      if (newSocket) {
        newSocket.on("connection_established", (data) => {
          if (data.onlineUsers && Array.isArray(data.onlineUsers)) {
            setOnlineUserIds(new Set(data.onlineUsers.map(String)));
          }
        });

        newSocket.on("connect", () => {
          setIsConnected(true);
          isConnectedRef.current = true;
          newSocket.emit("get_user_rooms");

          // Auto-join previous conversation if exists
          if (activeConversationRef.current) {
            setTimeout(() => {
              newSocket.emit("join_room", activeConversationRef.current);
            }, 500);
          }
        });

        // Socket event handlers
        newSocket.on("new_direct_message", handleIncomingDirectMessage);
        newSocket.on("direct_message_sent", handleDirectMessageSent);
        newSocket.on("new_group_message", handleIncomingGroupMessage);
        newSocket.on("group_message_sent", handleGroupMessageSent);
        newSocket.on("conversation_updated", handleConversationUpdated);
        newSocket.on("user_typing", handleUserTyping);
        newSocket.on("user_typing_group", handleGroupTyping);
        newSocket.on("direct_message_read", handleMessageRead);
        newSocket.on("user_online", handleUserOnline);
        newSocket.on("user_offline", handleUserOffline);
        newSocket.on("room_joined", handleRoomJoined);
        newSocket.on("latest_messages", handleLatestMessages);
        newSocket.on("message_deleted", handleRemoteMessageDeleted);
        newSocket.on("user_blocked", handleUserBlocked);
        newSocket.on("user_unblocked", handleUserUnblocked);
        newSocket.on("group_system_message", handleGroupSystemMessage);

        newSocket.on("reconnect", () => {
          console.log("🔁 Socket reconnected");
          setIsConnected(true);
          isConnectedRef.current = true;
          if (activeConversationRef.current) {
            newSocket.emit("join_room", activeConversationRef.current);
          }
          setTimeout(() => {
            if (activeTab === "direct") fetchDirectConversations();
            else if (activeTab === "groups") fetchGroups();
          }, 1000);
        });

        newSocket.on("disconnect", () => {
          console.log("🔌 Socket disconnected");
          setIsConnected(false);
          isConnectedRef.current = false;
        });

        newSocket.on("error", (error) => {
          console.error("❌ Socket error:", error);
          const msg = String(error?.message || error || "");
          if (msg.toLowerCase().includes("block")) {
            // Mark the other user as having blocked us so the UI updates
            setBlockedByUserIds((prev) => new Set([...prev, String(activeConversationRef.current)]));
            // Remove any stuck optimistic messages
            setMessages((prev) => prev.filter((m) => !m.isOptimistic));
            setIsSending(false);
          }
        });

        setSocket(newSocket);
        socketRef.current = newSocket;

        return () => {
          if (newSocket) {
            disconnectSocket();
          }
        };
      }
    };

    initializeSocket();
    return () => disconnectSocket();
  }, []);

  // Screen resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setShowSidebar(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // FIX 3: Initial data fetch on mount
  useEffect(() => {
    console.log("🚀 Initial data fetch started");
    const fetchInitialData = async () => {
      try {
        await Promise.all([
          fetchDirectConversations(),
          fetchAvailableUsers(),
          fetchBlockedUsers(),
          fetchUnreadCounts(),
        ]);

        // After initial data is loaded, check if we need to load messages
        if (initialActiveConversation && initialChatType) {
          console.log(
            `🎯 Loading initial conversation: ${initialChatType} - ${initialActiveConversation}`,
          );

          // Set active tab based on chat type
          if (initialChatType === "group") {
            setActiveTab("groups");
            // Wait for groups to load before fetching messages
            setTimeout(async () => {
              await fetchGroups();
              // Now fetch messages
              await fetchGroupMessages(initialActiveConversation);
            }, 500);
          } else if (initialChatType === "direct") {
            await fetchDirectMessages(initialActiveConversation);
          }
        }
      } catch (error) {
        console.error("Error in initial data fetch:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchInitialData();
  }, []); // Empty dependency array - runs only once on mount

  // Fetch data based on active tab (but not on initial load)
  useEffect(() => {
    if (!isInitialLoad) {
      if (activeTab === "direct") {
        fetchDirectConversations();
        fetchAvailableUsers();
        fetchBlockedUsers();
      } else if (activeTab === "groups") {
        fetchGroups();
      } else if (
        ["school", "vendor", "parent", "driver", "super_admin"].includes(
          activeTab,
        )
      ) {
        fetchUsersByRole(activeTab);
      }
      fetchUnreadCounts();
    }
  }, [activeTab, isInitialLoad]);

  // FIX 4: Load messages when conversation changes (with proper cleanup)
  useEffect(() => {
    if (!activeConversation || !authUser?.id) {
      console.log("❌ No active conversation or auth user");
      return;
    }

    // Debounce function to prevent rapid calls
    const debouncedLoadMessages = async () => {
      console.log(
        `🔄 Loading messages for ${currentChatType}: ${activeConversation}`,
      );

      setHasLoadedMessages(false);
      setMessages([]);
      setShouldAutoScroll(true);

      if (currentChatType === "direct") {
        await fetchDirectMessages(activeConversation);
      } else if (currentChatType === "group") {
        await fetchGroupMessages(activeConversation);
      }

      if (socketRef.current && isConnectedRef.current) {
        socketRef.current.emit("join_room", activeConversation);
      }
    };

    // Clear any existing timeout
    const timeoutId = setTimeout(() => {
      if (!isLoadingRef.current) {
        debouncedLoadMessages();
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [activeConversation, currentChatType, authUser?.id]);

  // FIX 5: Auto-load persisted group on mount
  useEffect(() => {
    const loadPersistedGroup = async () => {
      if (initialChatType === "group" && initialActiveConversation) {
        console.log(
          `📂 Loading persisted GROUP conversation: ${initialActiveConversation}`,
        );

        // First load groups
        await fetchGroups();

        // Wait for groups to be set in state
        setTimeout(() => {
          const groupExists = groupsRef.current.find(
            (g) => g.id === parseInt(initialActiveConversation),
          );
          if (groupExists) {
            console.log(`✅ Found persisted group: ${groupExists.name}`);
            setActiveConversation(initialActiveConversation);
            setCurrentChatType("group");
            setActiveTab("groups");
          } else {
            // Clear invalid persisted state
            clearChatState();
            setActiveConversation(null);
          }
        }, 1000);
      }
    };

    if (isInitialLoad) {
      loadPersistedGroup();
    }
  }, [isInitialLoad, initialChatType, initialActiveConversation]);

  // Scroll handling
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    const handleScroll = () => {
      const scrollTop = messagesContainer.scrollTop;
      const scrollHeight = messagesContainer.scrollHeight;
      const clientHeight = messagesContainer.clientHeight;
      const isAtBottom = scrollHeight - clientHeight - scrollTop < 50;
      setShowScrollButton(!isAtBottom);
      setShouldAutoScroll(isAtBottom);
    };

    messagesContainer.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => messagesContainer.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (shouldAutoScroll && messages.length > 0 && !loading.messages) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [messages, shouldAutoScroll, lastMessageUpdate, loading.messages]);

  // ==================== API FUNCTIONS ====================

  const fetchDirectConversations = async () => {
    try {
      setLoading((prev) => ({ ...prev, conversations: true }));
      const response = await getDirectConversations();
      if (response.success) {
        const conversations = response.data.conversations || [];
        const sorted = conversations.sort((a, b) => {
          const timeA = new Date(a.lastMessageAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.lastMessageAt || b.createdAt || 0).getTime();
          return timeB - timeA;
        });
        setDirectConversations(sorted);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading((prev) => ({ ...prev, conversations: false }));
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const response = await getUsersForChat();
      if (response.success) {
        const users = response.data.users || [];
        setAvailableUsers(users);
        setFilteredAvailableUsers(users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const fetchUsersByRole = async (role) => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      setSearchQuery("");
      const response = await getUsersByRole(role);
      if (response.success) {
        const users = response.data.users || [];
        setAvailableUsers(users);
        setFilteredAvailableUsers(users);
      } else {
        setAvailableUsers([]);
        setFilteredAvailableUsers([]);
      }
    } catch (error) {
      console.error(`Error fetching ${role} users:`, error);
      setAvailableUsers([]);
      setFilteredAvailableUsers([]);
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  // FIX 6: Enhanced direct messages fetch with type safety
  const fetchDirectMessages = async (userId) => {
    try {
      // Prevent duplicate calls
      const now = Date.now();
      if (now - lastFetchTime < 1000 && isLoadingRef.current) {
        console.log("⏳ Skipping duplicate fetch request");
        return;
      }

      setLoading((prev) => ({ ...prev, messages: true }));
      isLoadingRef.current = true;
      setLastFetchTime(now);

      console.log(`📨 FETCHING DIRECT MESSAGES for user: ${userId}`);
      console.log(`👤 Current user ID: ${authUser?.id}`);
      console.log(`📝 Chat Type: direct (should NOT have group messages)`);

      const response = await getDirectMessages(userId);
      if (response.success) {
        const apiMessages = response.data.messages || [];
        console.log(`✅ Loaded ${apiMessages.length} DIRECT messages`);

        // CRITICAL: Filter out any group messages (ensure they don't appear in direct chat)
        const directMessagesOnly = apiMessages.filter((msg) => {
          // Message should NOT have groupId, and should have receiverId
          const isDirectMessage = !msg.groupId && msg.receiverId;
          if (!isDirectMessage) {
            console.warn(`⚠️ Filtered out non-direct message:`, msg);
          }
          return isDirectMessage;
        });

        // Log message sequence for debugging
        directMessagesOnly.forEach((msg, index) => {
          console.log(
            `   ${index + 1}. ID: ${msg.id}, Sender: ${
              msg.senderId
            }, Receiver: ${msg.receiverId}, Content: "${msg.content?.substring(
              0,
              30,
            )}..."`,
          );
        });

        // Format messages with correct sender identification
        const currentUserId = authUser?.id || authUserIdRef.current;
        const formattedMessages = directMessagesOnly.map((msg) => {
          const isMe = parseInt(msg.senderId) === parseInt(currentUserId);
          return {
            ...msg,
            isMe: isMe,
            isOptimistic: false,
            isSelected: false,
            isDirect: true, // Explicit flag
            isGroup: false, // Explicit flag
            sender: msg.sender || {
              id: msg.senderId,
              name: msg.sender_name,
              avatar: msg.sender_avatar,
            },
          };
        });

        // Sort messages chronologically (oldest first)
        const sorted = formattedMessages.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateA - dateB; // Ascending order
        });

        console.log(
          `📊 Final: ${sorted.length} DIRECT messages ready to display`,
        );
        console.log(`✅ Setting messages state for DIRECT chat`);
        setMessages(sorted);
        setHasLoadedMessages(true);
        setLastMessageUpdate(Date.now());

        // Mark as read
        setTimeout(() => {
          if (socketRef.current) {
            socketRef.current.emit("mark_direct_message_read", {
              receiverId: userId,
            });
          }
          markAllDirectMessagesRead(userId);

          // Note: No immediate refetch here to avoid race condition
          // Optimistic update already handled in handleConversationClick
          // Backend sync will happen via socket events
        }, 1000);
      } else {
        console.error("Failed to fetch direct messages:", response.message);
        setMessages([]);
        setHasLoadedMessages(true);
      }
    } catch (error) {
      console.error("Error fetching direct messages:", error);
      setMessages([]);
      setHasLoadedMessages(true);
    } finally {
      setLoading((prev) => ({ ...prev, messages: false }));
      isLoadingRef.current = false;
    }
  };

  // FIX 7: Enhanced groups fetch with proper state management
  const fetchGroups = async () => {
    try {
      setLoading((prev) => ({ ...prev, groups: true }));
      console.log("🔄 Fetching groups...");

      const response = await getUserGroups();
      if (response.success) {
        const groupsData = response.data.groups || [];
        console.log(`✅ Loaded ${groupsData.length} groups`);

        // Format groups with proper structure
        const formattedGroups = groupsData.map((group) => ({
          id: group.id,
          name: group.name || "Unnamed Group",
          description: group.description || "",
          avatar: group.avatar || null,
          created_by: group.created_by,
          last_message_id: group.last_message_id,
          last_message_at: group.last_message_at,
          created_at: group.created_at,
          updated_at: group.updated_at,
          is_active: group.is_active || 1,
          lastMessage: group.lastMessage || {
            content: group.last_message,
            senderId: group.last_message_sender_id,
            senderName: group.last_message_sender_name,
            createdAt: group.last_message_at,
          },
          lastMessageAt: group.last_message_at,
          unreadCount: group.unread_count || 0,
          members: group.members || [],
          member_count: group.member_count || 0,
          member_role: group.member_role || "member",
        }));

        const sorted = formattedGroups.sort((a, b) => {
          const timeA = new Date(
            a.lastMessageAt || a.created_at || 0,
          ).getTime();
          const timeB = new Date(
            b.lastMessageAt || b.created_at || 0,
          ).getTime();
          return timeB - timeA;
        });

        console.log(`📊 Setting ${sorted.length} groups to state`);
        setGroups(sorted);
        groupsRef.current = sorted;

        return sorted; // Return groups for use in auto-load
      }
      return [];
    } catch (error) {
      console.error("Error fetching groups:", error);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, groups: false }));
    }
  };

  // FIX 8: Enhanced group messages fetch with type safety
  const fetchGroupMessages = async (groupId) => {
    try {
      // Prevent duplicate calls
      const now = Date.now();
      if (now - lastFetchTime < 1000 && isLoadingRef.current) {
        console.log("⏳ Skipping duplicate fetch request");
        return;
      }

      setLoading((prev) => ({ ...prev, messages: true }));
      isLoadingRef.current = true;
      setLastFetchTime(now);

      const response = await getGroupMessages(groupId);
      console.log(`📋 Group messages API response:`, {
        success: response.success,
        messageCount: response.data?.messages?.length || 0,
      });

      if (response.success) {
        const apiMessages = response.data.messages || [];

        // CRITICAL: Filter out any direct messages (ensure they don't appear in group chat)
        const groupMessagesOnly = apiMessages.filter((msg) => {
          // Message should have groupId, and should NOT have receiverId (or should have it null)
          const isGroupMessage = msg.groupId && !msg.receiverId;
          if (!isGroupMessage) {
            console.warn(`⚠️ Filtered out non-group message:`, msg);
          }
          return isGroupMessage;
        });

        // Log message sequence for debugging
        groupMessagesOnly.forEach((msg, index) => {
          console.log(
            `   ${index + 1}. ID: ${msg.id}, Sender: ${msg.senderId}, Group: ${
              msg.groupId
            }, Content: "${msg.content?.substring(0, 30)}..."`,
          );
        });

        // Format messages with correct sender identification
        const currentUserId = authUser?.id || authUserIdRef.current;
        const formattedMessages = groupMessagesOnly.map((msg) => {
          const isMe = parseInt(msg.senderId) === parseInt(currentUserId);
          return {
            ...msg,
            isMe: isMe,
            isOptimistic: false,
            isSelected: false,
            isDirect: false, // Explicit flag
            isGroup: true, // Explicit flag
            sender: msg.sender || {
              id: msg.senderId,
              name: msg.sender_name || `User ${msg.senderId}`,
              avatar: msg.sender_avatar,
            },
          };
        });

        // Sort messages chronologically (oldest first)
        const sorted = formattedMessages.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateA - dateB; // Ascending order
        });
        setMessages(sorted);
        setHasLoadedMessages(true);
        setLastMessageUpdate(Date.now());

        // Mark as read
        setTimeout(() => {
          if (socketRef.current) {
            socketRef.current.emit("mark_group_message_read", {
              groupId: groupId,
            });
          }
          markAllMessagesAsRead(groupId, "group");

          // Note: No immediate refetch here to avoid race condition
          // Optimistic update already handled in handleGroupConversationClick
          // Backend sync will happen via socket events
        }, 1000);
      } else {
        setMessages([]);
        setHasLoadedMessages(true);
      }
    } catch (error) {
      setMessages([]);
      setHasLoadedMessages(true);
    } finally {
      setLoading((prev) => ({ ...prev, messages: false }));
      isLoadingRef.current = false;
    }
  };

  // FIX 9: Enhanced load all group messages function
  const fetchAllGroupMessages = async (groupId) => {
    try {
      setLoading((prev) => ({ ...prev, messages: true }));
      const response = await getAllGroupMessages(groupId);
      if (response.success) {
        const apiMessages = response.data.messages || [];

        // Filter to ensure only group messages
        const groupMessagesOnly = apiMessages.filter(
          (msg) => msg.groupId && !msg.receiverId,
        );

        const currentUserId = authUser?.id || authUserIdRef.current;
        const formattedMessages = groupMessagesOnly.map((msg) => {
          const isMe = parseInt(msg.senderId) === parseInt(currentUserId);
          return {
            ...msg,
            isMe: isMe,
            isOptimistic: false,
            isSelected: false,
            isDirect: false,
            isGroup: true,
            sender: msg.sender || {
              id: msg.senderId,
              name: msg.sender_name,
              avatar: msg.sender_avatar,
            },
          };
        });

        const sorted = formattedMessages.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );

        setMessages(sorted);
        setLastMessageUpdate(Date.now());
      } else {
        console.error("Failed to fetch all group messages");
      }
    } catch (error) {
      console.error("Error fetching all group messages:", error);
    } finally {
      setLoading((prev) => ({ ...prev, messages: false }));
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await getUnreadCounts();
      if (response.success) {
        setUnreadCounts(response.data);
      }
    } catch (error) {
      console.error("Error fetching unread counts:", error);
    }
  };

  const fetchBlockedUsers = async () => {
    try {
      const response = await getBlockedUsers();
      if (response.success) {
        setBlockedUsers(response.data.blockedUsers || []);
        const blockedByIds = (response.data.blockedByUsers || []).map((u) => String(u?.id || "")).filter(Boolean);
        setBlockedByUserIds(new Set(blockedByIds));
      }
    } catch (error) {
      console.error("Error fetching blocked users:", error);
    }
  };

  // ==================== GROUP MANAGEMENT ====================

  const handleCreateGroup = async () => {
    if (!groupName.trim() || groupMembers.length === 0) {
      return;
    }

    setIsCreatingGroup(true);
    try {
      const response = await createGroupChat({
        name: groupName.trim(),
        participantIds: groupMembers.map((id) => parseInt(id)),
        description: "",
      });

      if (response.success) {
        setShowCreateGroupModal(false);
        setGroupName("");
        setGroupMembers([]);
        setActiveTab("groups");

        if (response.data?.group) {
          toast.success(response.message);
          const newGroup = {
            ...response.data.group,
            members: response.data.members || [],
            unreadCount: 0,
            lastMessageAt: new Date().toISOString(),
            member_role: "admin",
          };
          setGroups((prev) => [newGroup, ...prev]);
          groupsRef.current = [newGroup, ...groupsRef.current];
        }
      } else {
        toast.error(response.message || "Failed to create group.");
      }
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error(error?.message || "Failed to create group.");
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const handleLeaveGroup = async (groupId) => {
    try {
      setIsLeaveLoading(true);
      const response = await leaveGroup(groupId);
      fetchGroups(); // Refresh groups list
      if (response.success) {
        toast.success(response.message);
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        groupsRef.current = groupsRef.current.filter((g) => g.id !== groupId);
        if (activeConversation === groupId) {
          setActiveConversation(null);
          clearChatState();
        }
      } else {
        // alert(response.message || "Failed to leave group");
      }
    } catch (error) {
      console.error("Error leaving group:", error);
    } finally {
      setIsLeaveLoading(false);
      setShowLeaveModal(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      setIsDeleteLoading(true);
      const response = await deleteGroup(groupId);
      if (response.success) {
        toast.success(response.message);
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
        groupsRef.current = groupsRef.current.filter((g) => g.id !== groupId);
        if (activeConversation === groupId) {
          setActiveConversation(null);
          clearChatState();
        }
        fetchGroups(); // Refresh groups list
      } else {
        // alert(response.message || "Failed to delete group");
      }
    } catch (error) {
      console.error("Error deleting group:", error);
    } finally {
      setIsDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleEditGroup = async () => {
    if (!editGroupName.trim()) {
      setEditGroupError("Group name is required");
      return;
    }

    try {
      setEditGroupLoading(true);
      setEditGroupError("");

      const response = await updateGroup(editGroupData.id, {
        name: editGroupName.trim(),
        description: editGroupData.description || "",
      });

      if (response.success) {
        toast.success(response.message);

        setShowEditGroupModal(false);
        fetchGroups();
      } else {
        // setEditGroupError(response.message || "Failed to update group");
      }
    } catch (error) {
      console.error("Error updating group:", error);
      setEditGroupError(error.message || "Failed to update group");
    } finally {
      setEditGroupLoading(false);
    }
  };

  // ==================== MEMBER MANAGEMENT ====================

  const handleRemoveMember = async (groupId, memberId) => {
    try {
      const response = await removeGroupMember(groupId, memberId);
      if (response.success) {
        toast.success(response.message);

        // Update local groups state
        setGroups((prev) =>
          prev.map((group) => {
            if (group.id === groupId) {
              const updatedMembers = group.members?.filter((m) => {
                const mId = m.user_id || m.id;
                return mId !== memberId;
              });
              return {
                ...group,
                members: updatedMembers,
                member_count: updatedMembers?.length || 0,
              };
            }
            return group;
          }),
        );

        // Refresh groups list
        setTimeout(() => {
          fetchGroups();
        }, 500);
      } else {
        // alert(response.message || "Failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
    }
  };

  // ==================== FIXED: MESSAGE MANAGEMENT FUNCTIONS ====================

  // FIXED: Delete all messages (for everyone)
  const handleDeleteAllMessages = async () => {
    if (!activeConversation) return;

    try {
      setActionLoading(true);
      let response;

      if (currentChatType === "direct") {
        response = await deleteAllDirectMessages(activeConversation);
      } else {
        // Check if user is admin for group message deletion
        const currentGroup = groups.find(
          (g) => parseInt(g.id) === parseInt(activeConversation),
        );
        const isAdmin = currentGroup?.member_role === "admin";

        if (!isAdmin) {
          setActionLoading(false);
          setShowDeleteAllModal(false);
          return;
        }

        response = await deleteAllGroupMessages(activeConversation);
      }

      if (response.success) {
        toast.success(response?.message);

        setMessages([]);
      } else {
        // alert(response.message || "Failed to delete messages");
      }
    } catch (error) {
      console.error("Error deleting all messages:", error);
    } finally {
      setActionLoading(false);
      setShowDeleteAllModal(false);
    }
  };

  // FIXED: Clear chat history (for current user only) - UPDATED
  const handleClearChatHistory = async () => {
    if (!activeConversation) return;

    try {
      setActionLoading(true);

      let response;
      let successMessage = "";

      if (currentChatType === "direct") {
        response = await clearConversationHistory(activeConversation);

        if (response && response.success) {
          // Clear messages from view
          setMessages([]);

          // Update conversation in sidebar (remove last message preview)
          setDirectConversations((prev) =>
            prev.map((c) => {
              if (parseInt(c.otherUser?.id) === parseInt(activeConversation)) {
                return {
                  ...c,
                  lastMessage: null,
                  lastMessageAt: null,
                  unreadCount: 0,
                };
              }
              return c;
            }),
          );

          successMessage =
            "Chat history has been cleared from your view. The other user can still see the messages.";
        }
      } else if (currentChatType === "group") {
        response = await clearGroupHistory(activeConversation);

        if (response && response.success) {
          // Clear group messages from view
          setMessages([]);

          // Update group in list
          setGroups((prev) =>
            prev.map((g) =>
              g.id === parseInt(activeConversation)
                ? {
                    ...g,
                    lastMessage: null,
                    lastMessageAt: null,
                    unreadCount: 0,
                  }
                : g,
            ),
          );

          successMessage =
            "Group history has been cleared from your view. Other group members can still see the messages.";
        }
      }

      if (response && response.success) {
        toast.success(successMessage);
      } else if (response && response.message) {
        toast.success(response.message);
      } else {
        toast.error(t("chat_history_clear_failed"));
      }
    } catch (error) {
      console.error("Error clearing chat history:", error);
    } finally {
      setActionLoading(false);
      setShowClearChatModal(false);
    }
  };

  // FIXED: Delete conversation (for current user only - removes from conversation list) - UPDATED
  const handleDeleteConversation = async () => {
    if (!activeConversation || currentChatType !== "direct") return;

    // // Confirm with user
    // const confirmDelete = window.confirm(
    //   "Are you sure you want to delete this conversation permanently?\n\n" +
    //     "✓ This will remove the conversation from your chat list\n" +
    //     "✓ You will no longer see any messages from this conversation\n" +
    //     "✓ The other user will still have the conversation in their chat list\n" +
    //     "✓ This action cannot be undone",
    // );

    // if (!confirmDelete) return;

    try {
      setActionLoading(true);

      const response = await deleteConversationForUser(activeConversation);

      if (response.success) {
        // Remove conversation from sidebar
        setDirectConversations((prev) =>
          prev.filter(
            (c) => parseInt(c.otherUser?.id) !== parseInt(activeConversation),
          ),
        );

        // Clear current messages view
        setMessages([]);

        // Reset active conversation
        setActiveConversation(null);
        clearChatState();

        // Update unread counts
        fetchUnreadCounts();

        // Show success message
        // alert(
        //   "✅ Conversation deleted successfully.\n\n" +
        //     "✓ It has been removed from your chat list\n" +
        //     "✓ The other user can still see the conversation\n" +
        //     "✓ If you want to chat again, you can search for the user",
        // );
      } else {
        // alert(response.message || "Failed to delete conversation");
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!activeConversation) return;

    try {
      setActionLoading(true);
      const response = await markAllMessagesAsRead(
        activeConversation,
        currentChatType,
      );

      if (response.success) {
        // Update local messages
        setMessages((prev) => prev.map((msg) => ({ ...msg, isRead: true })));

        // Refresh conversations/groups and unread counts
        if (currentChatType === "direct") {
          fetchDirectConversations();
        } else if (currentChatType === "group") {
          fetchGroups();
        }
        fetchUnreadCounts();
      } else {
        // alert(response.message || "Failed to mark messages as read");
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setActionLoading(false);
      setShowMarkAllReadModal(false);
    }
  };

  const handleDeleteMessage = async (messageId, deleteForEveryone = false) => {
    setActionLoading(true);
    try {
      const response = await deleteMessage(messageId, deleteForEveryone);
      if (response.success) {
        if (deleteForEveryone) {
          // Show "deleted" placeholder for sender too, then notify others via socket
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    content: "This message was deleted",
                    isDeleted: true,
                  }
                : msg,
            ),
          );
          markConversationLastMessageDeleted(messageId);
          if (socketRef.current) {
            socketRef.current.emit("message_deleted", {
              messageId,
              roomId: activeConversation,
              deleteForEveryone: true,
            });
          }
        } else {
          // Delete for me only — remove from local list
          setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        }
      } else {
        // alert("Failed to delete message");
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    } finally {
      setShowDeleteMessageModal(false);
      setMessageToDelete(null);
      setDeleteForEveryone(false);
      setActionLoading(false);
    }
  };

  const handleDeleteSelectedMessages = async () => {
    if (selectedMessages.length === 0) return;

    try {
      setActionLoading(true);
      const response = await deleteMultipleMessages(
        selectedMessages.map((msg) => msg.id),
        deleteForEveryone,
      );

      if (response.success) {
        setMessages((prev) =>
          prev.filter(
            (msg) =>
              !selectedMessages.some((selected) => selected.id === msg.id),
          ),
        );
        setSelectedMessages([]);
        setShowSelectMessages(false);
      } else {
        // alert("Failed to delete selected messages");
      }
    } catch (error) {
      console.error("Error deleting selected messages:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMessageAction = (message, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Toggle: clicking the same message's dots closes the dropdown
    if (showMessageActions && selectedMessage?.id === message.id) {
      setShowMessageActions(false);
      setSelectedMessage(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    setMessageActionPosition({
      x: rect.right,
      y: rect.bottom,
    });
    setSelectedMessage(message);
    setShowMessageActions(true);
  };

  const confirmDeleteMessage = (message, forEveryone = false) => {
    setMessageToDelete(message.id);
    setDeleteForEveryone(forEveryone);
    setShowDeleteMessageModal(true);
    setShowMessageActions(false);
  };

  const handleToggleMessageSelect = (message) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === message.id ? { ...msg, isSelected: !msg.isSelected } : msg,
      ),
    );

    if (selectedMessages.some((m) => m.id === message.id)) {
      setSelectedMessages((prev) => prev.filter((m) => m.id !== message.id));
    } else {
      setSelectedMessages((prev) => [...prev, message]);
    }
  };

  // ==================== BLOCK MANAGEMENT ====================

  const handleBlockUser = async () => {
    if (!userToBlock) return;

    try {
      setBlockLoading(true);
      const response = await blockUser(userToBlock, blockReason);

      if (response.success) {
        setShowBlockModal(false);
        setUserToBlock(null);
        setBlockReason("");
        fetchBlockedUsers();
        fetchDirectConversations();
      } else {
        // alert(response.message || "Failed to block user");
      }
    } catch (error) {
      console.error("Error blocking user:", error);
    } finally {
      setBlockLoading(false);
    }
  };

  const handleUnblockUser = async () => {
    if (!userToUnblock) return;

    try {
      setUnBlockLoading(true);
      const response = await unblockUser(userToUnblock);

      if (response.success) {
        setShowUnblockModal(false);
        setUserToUnblock(null);
        fetchBlockedUsers();
        fetchDirectConversations();
      } else {
        // alert(response.message || "Failed to unblock user");
      }
    } catch (error) {
      console.error("Error unblocking user:", error);
    } finally {
      setUnBlockLoading(false);
    }
  };

  // ==================== FIXED: SOCKET EVENT HANDLERS ====================

  const handleIncomingDirectMessage = useCallback((data) => {
    const { message: newMessage } = data;
    const currentActiveConv = activeConversationRef.current;
    const currentChatTypeValue = currentChatTypeRef.current;
    const currentAuthUserId = authUserIdRef.current;

    console.log(
      `📥 INCOMING DIRECT MESSAGE: Sender ${newMessage.senderId}, Receiver ${newMessage.receiverId}`,
    );
    console.log(
      `📥 Current: ${currentActiveConv}, Type: ${currentChatTypeValue}`,
    );

    // Check if message is for current DIRECT conversation
    const isForCurrentConversation =
      currentActiveConv &&
      currentChatTypeValue === "direct" &&
      (parseInt(newMessage.senderId) === parseInt(currentActiveConv) ||
        parseInt(newMessage.receiverId) === parseInt(currentActiveConv));

    // CRITICAL: Only add if it's a direct message (no groupId)
    const isDirectMessage = !newMessage.groupId && newMessage.receiverId;

    if (isForCurrentConversation && isDirectMessage) {
      console.log(`✅ Message belongs to current DIRECT conversation`);

      setMessages((prev) => {
        const duplicate = prev.find((msg) => msg.id === newMessage.id);
        if (duplicate) {
          console.log(`⏭️ Duplicate message, skipping`);
          return prev;
        }

        const updated = [
          ...prev,
          {
            ...newMessage,
            isMe: parseInt(newMessage.senderId) === parseInt(currentAuthUserId),
            isOptimistic: false,
            isSelected: false,
            isDirect: true,
            isGroup: false,
          },
        ];

        // Sort chronologically
        const sorted = updated.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateA - dateB;
        });

        console.log(`📊 Added DIRECT message, total: ${sorted.length}`);
        return sorted;
      });

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }

    // Update conversation list
    setDirectConversations((prev) => {
      const otherUserId =
        parseInt(newMessage.senderId) === parseInt(currentAuthUserId)
          ? newMessage.receiverId
          : newMessage.senderId;

      let updated = [...prev];
      const existingIndex = updated.findIndex(
        (c) => c.otherUser?.id === parseInt(otherUserId),
      );

      if (existingIndex >= 0) {
        const isIncomingFromOther =
          parseInt(newMessage.senderId) !== parseInt(currentAuthUserId);
        const isActiveConv = isForCurrentConversation && isIncomingFromOther;

        // Conversation is open and message arrived — reset unread to 0 in DB
        if (isActiveConv && socketRef.current) {
          socketRef.current.emit("reset_conversation_unread", {
            otherUserId: parseInt(otherUserId),
          });
        }

        updated[existingIndex] = {
          ...updated[existingIndex],
          lastMessage: {
            id: newMessage.id,
            content: newMessage.content,
            senderId: newMessage.senderId,
            senderName: newMessage.sender?.name,
            createdAt: newMessage.createdAt,
            isDeleted: false,
          },
          lastMessageAt: newMessage.createdAt,
          unreadCount: isActiveConv
            ? 0
            : isIncomingFromOther
              ? (updated[existingIndex].unreadCount || 0) + 1
              : updated[existingIndex].unreadCount,
        };
      } else {
        updated.unshift({
          id: `conv_${otherUserId}`,
          otherUser: {
            id: otherUserId,
            name: newMessage.sender?.name || `User ${otherUserId}`,
            online: false,
          },
          lastMessage: {
            id: newMessage.id,
            content: newMessage.content,
            senderId: newMessage.senderId,
            senderName: newMessage.sender?.name,
            createdAt: newMessage.createdAt,
            isDeleted: false,
          },
          lastMessageAt: newMessage.createdAt,
          unreadCount:
            parseInt(newMessage.senderId) !== parseInt(currentAuthUserId)
              ? 1
              : 0,
        });
      }

      const sorted = updated.sort(
        (a, b) =>
          new Date(b.lastMessageAt || 0).getTime() -
          new Date(a.lastMessageAt || 0).getTime(),
      );

      return sorted;
    });

    fetchUnreadCounts();
  }, []);

  const handleIncomingGroupMessage = useCallback((data) => {
    const { message: newMessage } = data;
    const currentActiveConv = activeConversationRef.current;
    const currentChatTypeValue = currentChatTypeRef.current;
    const currentAuthUserId = authUserIdRef.current;

    console.log(
      `📥 INCOMING GROUP MESSAGE: Group ${newMessage.groupId}, Sender ${newMessage.senderId}`,
    );
    console.log(
      `📥 Current: ${currentActiveConv}, Type: ${currentChatTypeValue}`,
    );

    // Check if message is for current GROUP conversation
    const isForCurrentConversation =
      currentActiveConv &&
      currentChatTypeValue === "group" &&
      parseInt(currentActiveConv) === parseInt(newMessage.groupId);

    // CRITICAL: Only add if it's a group message (has groupId)
    const isGroupMessage = newMessage.groupId && !newMessage.receiverId;

    if (isForCurrentConversation && isGroupMessage) {
      console.log(`✅ Message belongs to current GROUP`);

      setMessages((prev) => {
        const duplicate = prev.find((msg) => msg.id === newMessage.id);
        if (duplicate) {
          console.log(`⏭️ Duplicate message, skipping`);
          return prev;
        }

        const updated = [
          ...prev,
          {
            ...newMessage,
            isMe: parseInt(newMessage.senderId) === parseInt(currentAuthUserId),
            isOptimistic: false,
            isSelected: false,
            isDirect: false,
            isGroup: true,
          },
        ];

        // Sort chronologically
        const sorted = updated.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateA - dateB;
        });

        console.log(`📊 Added GROUP message, total: ${sorted.length}`);
        return sorted;
      });

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }

    // Update groups list
    setGroups((prev) => {
      let updated = [...prev];
      const existingIndex = updated.findIndex(
        (g) => g.id === parseInt(newMessage.groupId),
      );

      if (existingIndex >= 0) {
        updated[existingIndex] = {
          ...updated[existingIndex],
          lastMessage: {
            id: newMessage.id,
            content: newMessage.content,
            senderId: newMessage.senderId,
            senderName: newMessage.sender?.name,
            createdAt: newMessage.createdAt,
            isDeleted: false,
          },
          lastMessageAt: newMessage.createdAt,
          unreadCount:
            parseInt(newMessage.senderId) !== parseInt(currentAuthUserId)
              ? (updated[existingIndex].unreadCount || 0) + 1
              : updated[existingIndex].unreadCount,
        };
      }

      const sorted = updated.sort(
        (a, b) =>
          new Date(b.lastMessageAt || 0).getTime() -
          new Date(a.lastMessageAt || 0).getTime(),
      );

      return sorted;
    });

    fetchUnreadCounts();
  }, []);

  const handleDirectMessageSent = useCallback((data) => {
    const { message: sentMessage } = data;
    console.log(`✅ DIRECT message sent confirmation received`);

    setMessages((prev) =>
      prev.map((msg) => {
        if (
          msg.isOptimistic &&
          msg.content === sentMessage.content &&
          parseInt(msg.senderId) === parseInt(sentMessage.senderId) &&
          !msg.groupId &&
          msg.receiverId
        ) {
          console.log(
            `🔄 Replacing optimistic DIRECT message with real message`,
          );
          return {
            ...sentMessage,
            isMe: true,
            isOptimistic: false,
            isSelected: false,
            isDirect: true,
            isGroup: false,
          };
        }
        return msg;
      }),
    );
    setIsSending(false);
  }, []);

  const handleGroupMessageSent = useCallback((data) => {
    const { message: sentMessage } = data;
    console.log(`✅ GROUP message sent confirmation received`);

    setMessages((prev) =>
      prev.map((msg) => {
        if (
          msg.isOptimistic &&
          msg.content === sentMessage.content &&
          parseInt(msg.senderId) === parseInt(sentMessage.senderId) &&
          msg.groupId &&
          !msg.receiverId
        ) {
          console.log(
            `🔄 Replacing optimistic GROUP message with real message`,
          );
          return {
            ...sentMessage,
            isMe: true,
            isOptimistic: false,
            isSelected: false,
            isDirect: false,
            isGroup: true,
          };
        }
        return msg;
      }),
    );
    setIsSending(false);
  }, []);

  const handleConversationUpdated = useCallback(
    (data) => {
      setTimeout(() => {
        if (activeTab === "direct") fetchDirectConversations();
        else if (activeTab === "groups") fetchGroups();
        fetchUnreadCounts();
      }, 300);
    },
    [activeTab],
  );

  const handleUserTyping = (data) => {
    if (data.userId !== authUser?.id && activeConversation === data.userId) {
      setIsTyping(true);
      setTimeout(() => setIsTyping(false), 2000);
    }
  };

  const handleGroupTyping = (data) => {
    if (data.userId !== authUser?.id && data.groupId === activeConversation) {
      setTypingUsers((prev) => ({
        ...prev,
        [data.userId]: {
          name: data.userName,
          isTyping: data.isTyping,
        },
      }));

      if (!data.isTyping) {
        setTimeout(() => {
          setTypingUsers((prev) => {
            const updated = { ...prev };
            delete updated[data.userId];
            return updated;
          });
        }, 2000);
      }
    }
  };

  const handleMessageRead = (data) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === data.messageId ? { ...msg, isRead: true } : msg,
      ),
    );

    // Note: Optimistic update already handled the badge count
    // Only update global unread counts, not individual conversations
    fetchUnreadCounts();
  };

  const handleUserOnline = (data) => {
    setOnlineUserIds((prev) => new Set([...prev, String(data.userId)]));
  };

  const handleUserOffline = (data) => {
    setOnlineUserIds((prev) => {
      const next = new Set(prev);
      next.delete(String(data.userId));
      return next;
    });
  };

  const handleRoomJoined = (data) => {
    console.log("✅ Joined room:", data);
  };

  const handleLatestMessages = (data) => {
    if (data.roomId === activeConversationRef.current) {
      const currentUserId = authUser?.id || authUserIdRef.current;
      const newMessages = data.messages.map((msg) => ({
        ...msg,
        isMe: parseInt(msg.sender_id) === parseInt(currentUserId),
        isOptimistic: false,
        isSelected: false,
        // Add type flags based on message structure
        isDirect: !msg.group_id && msg.receiver_id,
        isGroup: msg.group_id && !msg.receiver_id,
      }));

      setMessages((prev) => {
        const allMessages = [...prev, ...newMessages];
        const uniqueMessages = allMessages.filter(
          (msg, index, self) =>
            index === self.findIndex((m) => m.id === msg.id),
        );

        // Sort chronologically
        const sorted = uniqueMessages.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateA - dateB;
        });

        return sorted;
      });
    }
  };

  const handleGroupSystemMessage = (data) => {
    if (parseInt(data.groupId) !== parseInt(activeConversationRef.current)) return;
    setMessages((prev) => {
      const exists = prev.some((m) => m.id === data.id);
      if (exists) return prev;
      return [...prev, { ...data, isSystem: true }];
    });
    // If current user was removed, leave the group view
    const currentUserId = parseInt(authUser?.id || authUserIdRef.current);
    if (data.removedMemberId === currentUserId) {
      setActiveConversation(null);
      fetchGroups();
    }
  };

  const handleUserBlocked = ({ blockerId, blockedId }) => {
    const currentUserId = parseInt(authUser?.id || authUserIdRef.current);
    if (blockerId === currentUserId) {
      // I blocked someone — refresh my blocked list
      fetchBlockedUsers();
    } else if (blockedId === currentUserId) {
      // Someone blocked me — track it so UI disables input
      setBlockedByUserIds((prev) => new Set([...prev, String(blockerId)]));
    }
  };

  const handleUserUnblocked = ({ blockerId, blockedId }) => {
    const currentUserId = parseInt(authUser?.id || authUserIdRef.current);
    if (blockerId === currentUserId) {
      // I unblocked someone — refresh my blocked list
      fetchBlockedUsers();
    } else if (blockedId === currentUserId) {
      // Someone unblocked me — remove from blocked-by set
      setBlockedByUserIds((prev) => {
        const next = new Set(prev);
        next.delete(String(blockerId));
        return next;
      });
    }
  };

  const markConversationLastMessageDeleted = (messageId) => {
    setDirectConversations((prev) =>
      prev.map((conv) =>
        String(conv.lastMessage?.id) === String(messageId)
          ? {
              ...conv,
              lastMessage: {
                ...conv.lastMessage,
                content: "This message was deleted",
                isDeleted: true,
              },
            }
          : conv,
      ),
    );
  };

  const handleRemoteMessageDeleted = ({ messageId }) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, content: "This message was deleted", isDeleted: true }
          : msg,
      ),
    );
    markConversationLastMessageDeleted(messageId);
  };

  // ==================== FIXED: EVENT HANDLERS ====================

  const handleConversationClick = (conversationId) => {
    console.log(`💬 DIRECT conversation clicked: ${conversationId}`);
    console.log(`👤 Current user: ${authUser?.id}`);
    console.log(`📝 Setting chat type: direct`);

    setActiveConversation(conversationId);
    setCurrentChatType("direct");
    setActiveTab("direct"); // Ensure we're on the direct tab

    // Optimistic update: immediately reset unread count for this conversation
    setDirectConversations((prev) =>
      prev.map((conv) =>
        conv.otherUser?.id === conversationId
          ? { ...conv, unreadCount: 0 }
          : conv,
      ),
    );

    if (isMobile) setShowSidebar(false);
    if (socketRef.current) {
      socketRef.current.emit("join_room", conversationId);
      // Reset unread count in DB
      socketRef.current.emit("reset_conversation_unread", {
        otherUserId: conversationId,
      });
    }
  };

  const handleGroupConversationClick = (groupId) => {
    setActiveConversation(groupId);
    setCurrentChatType("group");
    setActiveTab("groups"); // Ensure we're on the groups tab

    // Optimistic update: immediately reset unread count for this group
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId ? { ...group, unreadCount: 0 } : group,
      ),
    );

    if (isMobile) setShowSidebar(false);
    if (socketRef.current) {
      socketRef.current.emit("join_room", groupId);
    }
  };

  const handleUserSelect = async (user) => {
    try {
      const response = await startDirectChat(user.id);
      if (response.success) {
        setActiveConversation(user.id);
        setCurrentChatType("direct");
        setActiveTab("direct"); // Switch to direct tab

        if (isMobile) setShowSidebar(false);
        fetchDirectConversations();

        if (socketRef.current) {
          socketRef.current.emit("join_room", user.id);
        }
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
    }
  };

  const handleAddMembers = (group) => {
    setSelectedGroupForMembers(group);
    setShowGroupMembers(true);
  };

  // FIX 10: Enhanced send message with better type handling
  const handleSend = async () => {
    if (!message.trim() || !activeConversation || !authUser?.id || isSending)
      return;

    if (currentChatType === "direct" && isUserBlocked(activeConversation)) return;

    const messageContent = message.trim();
    const tempId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    console.log(`📤 SENDING ${currentChatType.toUpperCase()} MESSAGE:`);
    console.log(`   To: ${activeConversation}`);
    console.log(`   Chat Type: ${currentChatType}`);
    console.log(`   Current User: ${authUser?.id}`);
    console.log(`   Content: "${messageContent}"`);

    // Create temporary message with proper type flags
    const tempMessage = {
      id: tempId,
      content: messageContent,
      messageType: "text",
      senderId: authUser.id,
      sender: {
        id: authUser.id,
        name: authUser.full_name,
        avatar: authUser.profile_image,
      },
      isMe: true,
      createdAt: new Date().toISOString(),
      isRead: false,
      isOptimistic: true,
      isSelected: false,
    };

    // CRITICAL: Add correct fields based on chat type
    if (currentChatType === "group") {
      tempMessage.groupId = activeConversation;
      tempMessage.receiverId = null; // Explicit null for group
      tempMessage.isDirect = false;
      tempMessage.isGroup = true;
      console.log(
        `✅ Setting as GROUP message to group: ${activeConversation}`,
      );
    } else if (currentChatType === "direct") {
      tempMessage.receiverId = activeConversation;
      tempMessage.groupId = null; // Explicit null for direct
      tempMessage.isDirect = true;
      tempMessage.isGroup = false;
      console.log(
        `✅ Setting as DIRECT message to user: ${activeConversation}`,
      );
    }

    // Optimistic update
    setMessages((prev) => {
      const newMessages = [...prev, tempMessage];
      // Sort chronologically
      return newMessages.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || 0);
        const dateB = new Date(b.createdAt || b.created_at || 0);
        return dateA - dateB;
      });
    });

    setMessage("");
    setIsSending(true);

    try {
      // Send via Socket
      if (socketRef.current && isConnectedRef.current) {
        console.log(`📤 Sending message via Socket.IO (${currentChatType})`);
        if (currentChatType === "direct") {
          socketRef.current.emit("send_direct_message", {
            receiverId: activeConversation,
            content: messageContent,
            messageType: "text",
          });
        } else {
          socketRef.current.emit("send_group_message", {
            groupId: activeConversation,
            content: messageContent,
            messageType: "text",
          });
        }

        // Fallback timeout
        setTimeout(() => {
          const stillOptimistic = messagesRef.current.find(
            (msg) => msg.id === tempId && msg.isOptimistic,
          );
          if (stillOptimistic) {
            console.log("⏰ Using REST fallback (socket timeout)");
            sendViaRESTFallback(messageContent, tempId, activeConversation);
          }
        }, 5000);
      } else {
        // Use REST API directly
        console.log(`📤 Sending message via REST API (Socket not connected)`);
        await sendViaRESTFallback(messageContent, tempId, activeConversation);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      handleSendError(tempId, error);
    }
  };

  const sendViaRESTFallback = async (
    messageContent,
    tempId,
    conversationId,
  ) => {
    try {
      let response;
      if (currentChatType === "direct") {
        console.log(`📤 REST: Sending DIRECT message to ${conversationId}`);
        response = await sendDirectMessage({
          receiverId: conversationId,
          content: messageContent,
          messageType: "text",
        });
      } else {
        console.log(
          `📤 REST: Sending GROUP message to group ${conversationId}`,
        );
        response = await sendGroupMessage({
          groupId: conversationId,
          content: messageContent,
          messageType: "text",
        });
      }

      if (response.success && response.data?.message) {
        const realMessage = response.data.message;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempId
              ? {
                  ...realMessage,
                  isMe: true,
                  isOptimistic: false,
                  isSelected: false,
                  isDirect: currentChatType === "direct",
                  isGroup: currentChatType === "group",
                }
              : msg,
          ),
        );

        // setTimeout(() => {
        //   if (currentChatType === "direct") fetchDirectConversations();
        //   else fetchGroups();
        // }, 500);
      }
    } catch (error) {
      console.error("REST API fallback error:", error);
      const errMsg = String(error?.message || error?.details || "");
      if (errMsg.toLowerCase().includes("block")) {
        setBlockedByUserIds((prev) => new Set([...prev, String(activeConversationRef.current)]));
      }
      throw error;
    } finally {
      setIsSending(false);
    }
  };

  const handleSendError = (tempId, error) => {
    const errMsg = String(error?.message || error?.details || "");
    if (errMsg.toLowerCase().includes("block")) {
      // Remove the stuck message immediately; UI will show blocked state
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
      setBlockedByUserIds((prev) => new Set([...prev, String(activeConversationRef.current)]));
      return;
    }

    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === tempId
          ? {
              ...msg,
              isOptimistic: false,
              isError: true,
              errorMessage: "Failed to send",
            }
          : msg,
      ),
    );

    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
    }, 3000);

    setIsSending(false);
  };

  const handleTyping = () => {
    const now = Date.now();
    if (
      now - lastTypingTime > 1000 &&
      socketRef.current &&
      isConnectedRef.current &&
      activeConversation
    ) {
      if (currentChatType === "direct") {
        socketRef.current.emit("direct_typing", {
          receiverId: activeConversation,
          isTyping: true,
        });
      } else if (currentChatType === "group") {
        socketRef.current.emit("group_typing", {
          groupId: activeConversation,
          isTyping: true,
        });
      }
      setLastTypingTime(now);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShouldAutoScroll(true);
  };

  // const formatTimeAgo = (dateString) => {
  //   if (!dateString) return "";
  //   let normalized = dateString;
  //   // MySQL "YYYY-MM-DD HH:MM:SS" — no timezone info, treat as UTC
  //   if (
  //     typeof dateString === "string" &&
  //     /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)
  //   ) {
  //     normalized = dateString.replace(" ", "T") + "Z";
  //   }
  //   const date = new Date(normalized);
  //   if (isNaN(date.getTime())) return "";
  //   const now = new Date();
  //   const diffMs = now - date;
  //   const diffMins = Math.floor(diffMs / 60000);
  //   const diffHours = Math.floor(diffMs / 3600000);
  //   const diffDays = Math.floor(diffMs / 86400000);

  //   if (diffMins < 1) return "Just now";
  //   if (diffMins < 60) return `${diffMins}m ago`;
  //   if (diffHours < 24) return `${diffHours}h ago`;
  //   if (diffDays < 7) return `${diffDays}d ago`;
  //   return date.toLocaleDateString();
  // };

  const getDateLabel = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (sameDay(date, today)) return "Today";
    if (sameDay(date, yesterday)) return "Yesterday";
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "long",
      year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getAvatar = ({ avatar = null, name }) => {
    if (avatar) {
      return (
        <img
          src={`${server}${avatar}`}
          alt={name}
          className="rounded-circle h-100"
        />
      );
    }
    if (!name) return "??";
    return name.substring(0, 2).toUpperCase();
  };

  const getCurrentConversation = () => {
    if (currentChatType === "direct") {
      return directConversations.find(
        (c) => parseInt(c.otherUser?.id) === parseInt(activeConversation),
      )?.otherUser;
    } else if (currentChatType === "group") {
      return groups.find(
        (g) => parseInt(g.id) === parseInt(activeConversation),
      );
    }
    return null;
  };

  const currentConversation = getCurrentConversation();

  // Filter users based on search
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredAvailableUsers(availableUsers);
    } else {
      const filtered = availableUsers.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredAvailableUsers(filtered);
    }
  }, [searchQuery, availableUsers]);

  // Close message actions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showMessageActions &&
        !e.target.closest(".message-actions-dropdown")
      ) {
        setShowMessageActions(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showMessageActions]);

  // Check if I blocked this user
  const iBlockedThisUser = (userId) =>
    blockedUsers.some((b) => parseInt(b.blocked_id) === parseInt(userId));

  // Check if they blocked me
  const theyBlockedThisUser = (userId) => blockedByUserIds.has(String(userId));

  // Check if user is blocked (either direction)
  const isUserBlocked = (userId) => iBlockedThisUser(userId) || theyBlockedThisUser(userId);
  useEffect(() => {
    if (activeConversation && currentChatType === "group") {
      setTimeout(() => {
        fetchAllGroupMessages(activeConversation);
      }, 2000);
    }
  }, [activeConversation, currentChatType]);

  return (
    <div className={`messaging-app ${isMobile ? "mobile" : ""}`}>
      {/* Mobile Header */}
      {false && isMobile && (
        <div className="mobile-header">
          <button
            className="menu-button"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <FaBars />
          </button>
          {currentConversation ? (
            <div className="current-chat-info">
              <div className="avatar">
                {getAvatar({ name: currentConversation.name })}
              </div>
              <div>
                <h3>
                  {currentConversation.name}
                  {currentChatType === "group" && (
                    <span
                      className="badge bg-primary ms-2"
                      style={{ fontSize: "0.7em" }}
                    >
                      {t("group")}
                    </span>
                  )}
                </h3>
                <small className="text-muted">
                  {currentChatType === "direct"
                    ? onlineUserIds.has(String(activeConversation))
                      ? t("online")
                      : t("offline")
                    : `${currentConversation.members?.length || 0} members`}
                </small>
              </div>
            </div>
          ) : (
            <span style={{ fontWeight: 500, color: "#232e35" }}>
              {t("user")} {t("communication")}
            </span>
          )}
        </div>
      )}

      {/* Mobile backdrop — click to close sidebar */}
      {isMobile && showSidebar && (
        <div
          className="chat-sidebar-backdrop"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`chatSidebar ${showSidebar ? "visible" : ""}`}>
        {/* Mobile close button inside sidebar header */}
        {isMobile && (
          <button
            className="chat-sidebar-close"
            onClick={() => setShowSidebar(false)}
          >
            <FaTimes />
          </button>
        )}

        {/* login user details */}
        <div className="user-profile">
          <div className="avatar">
            {getAvatar({
              avatar: authUser?.profile_image,
              name: authUser?.full_name,
            })}
          </div>
          <div className="user-info">
            <h3>{authUser?.full_name || "You"}</h3>
            <span className="status online">{t("online")}</span>
          </div>
        </div>

        <Tabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onAddGroup={() => setShowCreateGroupModal(true)}
          authUser={authUser}
          t={t}
        />

        {showGroupMembers && selectedGroupForMembers ? (
          <GroupMembersOverlay
            group={selectedGroupForMembers}
            authUser={authUser}
            onClose={() => {
              setShowGroupMembers(false);
              setSelectedGroupForMembers(null);
            }}
            onMembersUpdated={() => {
              fetchGroups();
            }}
            t={(key) => key}
          />
        ) : activeTab === "direct" ? (
          <div className="conversation-list">
            {loading.conversations ? (
              <ConversationSkeleton />
            ) : directConversations.length === 0 ? (
              <div className="empty-state text-center py-5">
                <div className="empty-icon mb-3">
                  <IoChatbubbleEllipsesSharp size={48} className="text-muted" />
                </div>
                <div>
                  <p className="text-muted">
                    {t("no")} {t("conversations")} {t("yet")}
                  </p>
                  <small className="text-muted">
                    {t("start")} {t("a")} {t("conversation")} {t("to")}{" "}
                    {t("see")} {t("it")} {t("here")}
                  </small>
                </div>
              </div>
            ) : (
              directConversations.map((conversation) => {
                const isBlocked = isUserBlocked(conversation.otherUser?.id);
                return (
                  <div
                    key={conversation.id}
                    className={`conversation cursor-pointer ${
                      activeConversation === conversation.otherUser?.id
                        ? "active"
                        : ""
                    } ${isBlocked ? "blocked" : ""}`}
                    onClick={() =>
                      !isBlocked &&
                      handleConversationClick(conversation.otherUser?.id)
                    }
                  >
                    <div className="avatar">
                      {getAvatar({
                        avatar: conversation.otherUser?.avatar,
                        name: conversation.otherUser?.name,
                      })}
                      {isBlocked && <span className="blocked-badge">🚫</span>}
                    </div>
                    <div className="conversation-info">
                      <div className="name-status">
                        <h4>{conversation.otherUser?.name}</h4>
                        {isBlocked && (
                          <span className="badge bg-danger ms-1">
                            {t("blocked")}
                          </span>
                        )}
                        {!isBlocked && onlineUserIds.has(String(conversation.otherUser?.id)) && (
                          <span className="online-dot"></span>
                        )}
                      </div>
                      <p className="last-message">
                        {isBlocked ? (
                          `${t("this")} ${t("user")} ${t("is")} ${t("blocked")}`
                        ) : conversation.lastMessage?.isDeleted ? (
                          <span style={{ fontStyle: "italic", color: "#999" }}>
                            🚫 This message was deleted
                          </span>
                        ) : (
                          <>
                            {parseInt(conversation.lastMessage?.senderId) ===
                            parseInt(authUser?.id)
                              ? `${t("you")}: `
                              : ""}
                            {conversation.lastMessage?.content ||
                              `${t("no")} ${t("messages")} ${t("yet")}`}
                          </>
                        )}
                      </p>

                      <div className="message-meta">
                        <span className="time">
                          {formatTimeAgo(conversation.lastMessageAt)}
                        </span>
                        {!isBlocked && conversation.unreadCount > 0 && (
                          <span className="unread-count">
                            {conversation.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="conversation-actions">
                      {iBlockedThisUser(conversation.otherUser?.id) ? (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserToUnblock(conversation.otherUser?.id);
                            setShowUnblockModal(true);
                          }}
                          title={`${t("unblock")} ${t("user")}`}
                        >
                          {t("unblock")}
                        </button>
                      ) : theyBlockedThisUser(conversation.otherUser?.id) ? (
                        <span
                          title="This user has blocked you"
                          style={{ color: "#dc3545", fontSize: "18px", padding: "0 6px" }}
                        >
                          <FaBan />
                        </span>
                      ) : (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserToBlock(conversation.otherUser?.id);
                            setShowBlockModal(true);
                          }}
                          title={`${t("block")} ${t("user")}`}
                        >
                          <FaBan />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        ) : activeTab === "groups" ? (
          <GroupConversations
            groups={groups}
            activeConversation={activeConversation}
            handleConversationClick={handleGroupConversationClick}
            isDeletingGroup={isDeleteLoading}
            authUser={authUser}
            onLeaveGroupClick={(groupId) => {
              setLeaveGroupId(groupId);
              setShowLeaveModal(true);
            }}
            onDeleteGroupClick={(groupId) => {
              setDeleteGroupId(groupId);
              setShowDeleteModal(true);
            }}
            onEditGroup={(group) => {
              setEditGroupData(group);
              setEditGroupName(group.name);
              setShowEditGroupModal(true);
            }}
            onViewMembers={(group) => {
              setSelectedGroupForMembers(group);
              setShowGroupMembers(true);
            }}
            onAddMembers={handleAddMembers}
            onRemoveMember={handleRemoveMember}
            refetchGroups={fetchGroups}
            searchTerm={searchQuery}
            onSearchChange={setSearchQuery}
            isLoading={loading.groups}
          />
        ) : (
          <div className="users-list-container">
            <div className="search-container mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={`${t("search")} ${t("users")}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </div>

            {loading.users ? (
              <UsersSkeleton />
            ) : filteredAvailableUsers.length === 0 ? (
              <div className=" d-flex justify-content-center align-items-center flex-column pt-5">
                <div className="empty-icon mb-3">
                  <FaUsersSlash size={48} className="text-muted" />
                </div>
                <p className="mb-1 text-muted">
                  {t("no")} {t("users")} {t("found")}
                </p>
                <small className="text-muted">
                  {searchQuery
                    ? `${t("try")} ${t("a")} ${t("different")} ${t("search")}`
                    : `${t("no")} ${t("users")} ${t("available")} ${t(
                        "for",
                      )} ${t("chat")}`}
                </small>
              </div>
            ) : (
              <div className="users-list">
                {filteredAvailableUsers.map((user) => {
                  const isBlocked = isUserBlocked(user.id);
                  const iBlockedUser = iBlockedThisUser(user.id);
                  const theyBlockedUser = theyBlockedThisUser(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`user-item cursor-pointer d-flex align-items-center justify-content-between px-4 py-3 border-bottom ${
                        activeConversation === user.id ? "active" : ""
                      } ${isBlocked ? "blocked" : ""}`}
                      onClick={() => !isBlocked && handleUserSelect(user)}
                    >
                      <div className="d-flex align-items-center">
                        {" "}
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            fontSize: "18px",
                            marginRight: "12px",
                            borderRadius: "50%",
                            backgroundColor: "#374954",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            flexShrink: 0,
                          }}
                        >
                          {getAvatar({ avatar: user.avatar, name: user.name })}
                          {isBlocked && (
                            <span className="blocked-badge">🚫</span>
                          )}
                        </div>
                        <div className="user-info">
                          <div className="user-name">
                            {user.name}
                            {isBlocked && (
                              <span className="badge bg-danger ms-2">
                                {t("blocked")}
                              </span>
                            )}
                          </div>
                          <div className="user-details">
                            <small className="text-muted">{user.email}</small>
                            {user.role && (
                              <small className="text-muted d-block">
                                {user.role.charAt(0).toUpperCase() +
                                  user.role.slice(1)}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="user-actions">
                        {iBlockedUser ? (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUserToUnblock(user.id);
                              setShowUnblockModal(true);
                            }}
                            title={`${t("unblock")} ${t("user")}`}
                          >
                            {t("unblock")}
                          </button>
                        ) : theyBlockedUser ? (
                          <span
                            title="This user has blocked you"
                            style={{ color: "#dc3545", fontSize: "18px", padding: "0 6px" }}
                          >
                            <FaBan />
                          </span>
                        ) : (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUserToBlock(user.id);
                              setShowBlockModal(true);
                            }}
                            title={`${t("block")} ${t("user")}`}
                          >
                            <FaBan />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="chat-area">
        {currentConversation ? (
          <>
            <div className="chat-header d-flex align-items-center justify-content-between">
              <div className="user-info d-flex align-items-center">
                {/* Mobile back button — opens contact list */}
                {isMobile && (
                  <button
                    className="chat-back-btn"
                    onClick={() => setShowSidebar(true)}
                  >
                    <FaBars />
                  </button>
                )}
                <div className="avatar">
                  {getAvatar({ name: currentConversation.name })}
                </div>
                <div>
                  <h3>
                    {currentConversation.name}
                    {currentChatType === "group" && (
                      <span
                        className="badge bg-primary ms-2"
                        style={{ fontSize: "0.7em" }}
                      >
                        {t("group")}
                      </span>
                    )}
                    {currentChatType === "direct" &&
                      isUserBlocked(activeConversation) && (
                        <span
                          className="badge bg-danger ms-2"
                          style={{ fontSize: "0.7em" }}
                        >
                          {t("blocked")}
                        </span>
                      )}
                  </h3>
                  <p className="status">
                    {currentChatType === "direct"
                      ? onlineUserIds.has(String(activeConversation))
                        ? t("online")
                        : t("offline")
                      : `${currentConversation.members?.length || 0} ${t(
                          "members",
                        )}`}

                    {isTyping &&
                      currentChatType === "direct" &&
                      ` • ${t("typing")}...`}

                    {Object.keys(typingUsers).length > 0 &&
                      currentChatType === "group" &&
                      ` • ${
                        Object.values(typingUsers).filter((u) => u.isTyping)
                          .length
                      } ${t("typing")}...`}
                  </p>
                </div>
              </div>
              <div className="connection-status d-flex align-items-center gap-2">
                {/* {isConnected ? (
                  <span className='badge bg-success'>Connected</span>
                ) : (
                  <span className='badge bg-warning'>Disconnected</span>
                )} */}

                {/* Chat Settings Button */}
                <Dropdown>
                  <Dropdown.Toggle variant="outline-secondary" size="sm">
                    <FaCog />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => setShowChatSettings(true)}>
                      <FaCog className="me-2" /> {t("chat")} {t("settings")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowSelectMessages(true)}>
                      <FaCheckDouble className="me-2" /> {t("select")}{" "}
                      {t("messages")}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => setShowMarkAllReadModal(true)}
                    >
                      <FaEnvelope className="me-2" /> {t("mark")} {t("all")}{" "}
                      {t("as")} {t("read")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowClearChatModal(true)}>
                      <FaHistory className="me-2" /> {t("clear")} {t("chat")}{" "}
                      {t("history")}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowDeleteAllModal(true)}>
                      <FaTrash className="me-2 text-danger" /> {t("delete")}{" "}
                      {t("all")} {t("messages")}
                    </Dropdown.Item>
                    {currentChatType === "group" && (
                      <Dropdown.Item
                        onClick={() => {
                          setShowChatSettings(false);
                          fetchAllGroupMessages(activeConversation);
                        }}
                      >
                        <FaDownload className="me-2" /> {t("load")} {t("all")}{" "}
                        {t("messages")}
                      </Dropdown.Item>
                    )}
                    {currentChatType === "direct" && (
                      <>
                        {/* <Dropdown.Divider />
                        <Dropdown.Item onClick={handleDeleteConversation} className="text-danger">
                          <FaTrashAlt className="me-2" />
                          <div>
                            <strong>Delete Conversation</strong>
                            <small className="d-block text-muted">
                              Remove from your chat list (other user keeps it)
                            </small>
                          </div>
                        </Dropdown.Item> */}
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>

                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    console.log(`🔄 Refreshing ${currentChatType} messages`);
                    if (currentChatType === "direct" && activeConversation) {
                      fetchDirectMessages(activeConversation);
                    } else if (
                      currentChatType === "group" &&
                      activeConversation
                    ) {
                      fetchGroupMessages(activeConversation);
                    }
                  }}
                  title={`${t("refresh")} ${t("messages")}`}
                  disabled={loading.messages}
                >
                  {loading.messages ? (
                    <div className="spinner-border small" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  ) : (
                    <FaSync />
                  )}
                </button>
                {currentChatType === "direct" && (
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => {
                      setUserToBlock(activeConversation);
                      setShowBlockModal(true);
                    }}
                    title={`${t("block")} ${t("user")}`}
                  >
                    <FaBan />
                  </button>
                )}
              </div>
            </div>

            {/* Selected Messages Actions Bar */}
            {showSelectMessages && selectedMessages.length > 0 && (
              <div
                style={{
                  backgroundColor: "#0000000d",
                  color: "#232e35",
                  paddingLeft: "",
                }}
                className="selected-messages-bar px-4 py-2 d-flex justify-content-between align-items-center"
              >
                <div>
                  <span className="fw-semibold">
                    {selectedMessages.length} {t("messages")} {t("selected")}
                  </span>
                </div>
                <div className="d-flex justify-content-center align-items-center">
                  <Button
                    variant="light"
                    size="sm"
                    className="me-2 border border-dark"
                    onClick={() => {
                      setSelectedMessages([]);
                      setShowSelectMessages(false);
                      setMessages((prev) =>
                        prev.map((msg) => ({ ...msg, isSelected: false })),
                      );
                    }}
                  >
                    {t("cancel")}
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle
                      style={{
                        backgroundColor: "#f0b342",
                        color: "#232e35",
                        border: "1px solid #f0b342",
                      }}
                      className="fw-semibold"
                      variant="light"
                      size="sm"
                    >
                      {t("actions")}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={handleDeleteSelectedMessages}>
                        <FaTrash className="me-2 text-danger" /> {t("delete")}{" "}
                        {t("selected")}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => setDeleteForEveryone(!deleteForEveryone)}
                      >
                        <input
                          type="checkbox"
                          checked={deleteForEveryone}
                          onChange={() =>
                            setDeleteForEveryone(!deleteForEveryone)
                          }
                          className="me-2"
                        />
                        {t("delete")} {t("for")} {t("everyone")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            )}

            {/* Messages Container */}
            <div className="messages-container" ref={messagesContainerRef}>
              {loading.messages && !hasLoadedMessages ? (
                <MessagesSkeleton />
              ) : messages.length === 0 ? (
                <div className="empty-messages text-center py-5">
                  <FaEnvelope size={48} className="text-muted mb-3" />
                  <p className="text-muted">
                    {t("no")} {t("messages")} {t("yet")}
                  </p>
                  <small className="text-muted">
                    {t("start")} {t("the")} {t("conversation")}!
                  </small>
                </div>
              ) : (
                <>
                  {/* Debug info for message types */}
                  {messages.some(
                    (m) => m.isDirect && currentChatType === "group",
                  ) && (
                    <div
                      className="alert alert-warning"
                      style={{ fontSize: "12px", padding: "5px 10px" }}
                    >
                      ⚠️ {t("directMessagesInGroup")}
                    </div>
                  )}

                  {messages.map((message, index) => {
                    const msgDate = message.createdAt
                      ? new Date(message.createdAt).toDateString()
                      : null;
                    const prevMsgDate =
                      index > 0 && messages[index - 1].createdAt
                        ? new Date(messages[index - 1].createdAt).toDateString()
                        : null;
                    const showDateSeparator =
                      msgDate && msgDate !== prevMsgDate;

                    return (
                    <div key={message.id || index}>
                      {showDateSeparator && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            margin: "16px 0 10px",
                            gap: "10px",
                          }}
                        >
                          <div style={{ flex: 1, height: "1px", backgroundColor: "#e8e8e8" }} />
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#888",
                              backgroundColor: "#f5f5f5",
                              padding: "3px 12px",
                              borderRadius: "12px",
                              whiteSpace: "nowrap",
                              border: "1px solid #e8e8e8",
                            }}
                          >
                            {getDateLabel(message.createdAt)}
                          </span>
                          <div style={{ flex: 1, height: "1px", backgroundColor: "#e8e8e8" }} />
                        </div>
                      )}

                    {/* System message (e.g. "X was removed by Admin") */}
                    {(message.isSystem || message.messageType === "system") ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          margin: "6px 0",
                          padding: "5px 14px",
                          fontSize: "12px",
                          color: "#888",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ flex: 1, height: "1px", backgroundColor: "#e8e8e8" }} />
                        <span
                          style={{
                            backgroundColor: "#f0f0f0",
                            borderRadius: "10px",
                            padding: "3px 12px",
                            whiteSpace: "nowrap",
                            fontStyle: "italic",
                          }}
                        >
                          {message.content}
                        </span>
                        <div style={{ flex: 1, height: "1px", backgroundColor: "#e8e8e8" }} />
                      </div>
                    ) : (
                    <div
                      className={`message ${
                        message.isMe ? "sent" : "received"
                      } ${message.isOptimistic ? "optimistic" : ""} ${
                        message.isError ? "error" : ""
                      } ${message.isSelected ? "selected" : ""}`}
                      onClick={() =>
                        showSelectMessages && handleToggleMessageSelect(message)
                      }
                      title={
                        message.isDirect
                          ? t("direct")
                          : message.isGroup
                            ? t("group")
                            : t("unknown")
                      }
                    >
                      {showSelectMessages && (
                        <div className="message-selector">
                          <input
                            type="checkbox"
                            checked={message.isSelected || false}
                            onChange={() => handleToggleMessageSelect(message)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <div className="message-content">
                        {currentChatType === "group" && !message.isMe && (
                          <div className="message-sender">
                            {message.sender?.name || "Unknown"}
                          </div>
                        )}
                        <div
                          className={`message-text${message.isDeleted ? " message-deleted-text" : ""}`}
                        >
                          {message.isDeleted ? (
                            <span
                              style={{ fontStyle: "italic", opacity: 0.65 }}
                            >
                              🚫 This message was deleted
                            </span>
                          ) : (
                            message.content
                          )}
                          {message.isOptimistic && (
                            <span className="optimistic-indicator">
                              <FaClock
                                className="fa-spin"
                                style={{
                                  fontSize: "10px",
                                  marginLeft: "5px",
                                }}
                              />
                            </span>
                          )}
                          {message.isError && (
                            <span
                              className="error-indicator"
                              style={{
                                color: "#dc3545",
                                fontSize: "10px",
                                marginLeft: "5px",
                              }}
                            >
                              ({t("failed")})
                            </span>
                          )}
                        </div>
                        <span className="message-time">
                          {formatTimeAgo(message.createdAt)}
                          {message.isRead && message.isMe && (
                            <FaCheckDouble
                              style={{
                                fontSize: "10px",
                                marginLeft: "5px",
                                color: "#34b7f1",
                              }}
                            />
                          )}
                          {/* {message.isOptimistic && ` (${t("sending")}...)`} */}
                        </span>
                      </div>

                      {!showSelectMessages && !message.isDeleted && (
                        <div className="message-actions">
                          <button
                            className="btn btn-sm btn-link"
                            onClick={(e) => handleMessageAction(message, e)}
                            title={`${t("message")} ${t("actions")}`}
                          >
                            <FaEllipsisV size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                    )}
                    </div>
                  );
                  })}

                  {showScrollButton && (
                    <button
                      className="scroll-to-bottom-btn"
                      onClick={scrollToBottom}
                      title={`${t("scroll")} ${t("to")} ${t("bottom")}`}
                    >
                      <FaArrowDown />
                    </button>
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Actions Dropdown */}
            {showMessageActions && selectedMessage && (
              <div
                className="message-actions-dropdown"
                style={{
                  position: "fixed",
                  left: messageActionPosition.x,
                  top: messageActionPosition.y,
                  transform: "translateX(-100%)",
                  zIndex: 1000,
                  backgroundColor: "white",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  minWidth: "150px",
                }}
              >
                <div
                  className="dropdown-item"
                  onClick={() => {
                    setShowMessageActions(false);
                    // Reply
                    // functionality
                  }}
                >
                  {/* <FaReply size={14} className="me-2" /> Reply */}
                </div>
                <div
                  className="dropdown-item"
                  onClick={() => {
                    setShowMessageActions(false);
                    handleToggleMessageSelect(selectedMessage);
                    setShowSelectMessages(true);
                  }}
                >
                  <FaCheckDouble size={14} className="me-2" /> {t("select")}
                </div>
                <div className="dropdown-divider"></div>
                <div
                  className="dropdown-item text-danger"
                  onClick={() => confirmDeleteMessage(selectedMessage, false)}
                >
                  <FaTrashAlt size={14} className="me-2" /> {t("delete")}{" "}
                  {t("for")} {t("me")}
                </div>
                {selectedMessage.isMe && (
                  <div
                    className="dropdown-item text-danger"
                    onClick={() => confirmDeleteMessage(selectedMessage, true)}
                  >
                    <FaTrashAlt size={14} className="me-2" /> {t("delete")}{" "}
                    {t("for")} {t("everyone")}
                  </div>
                )}
              </div>
            )}

            {/* Blocked banner — shown above input when direct chat is blocked */}
            {currentChatType === "direct" && isUserBlocked(activeConversation) && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 16px",
                  backgroundColor: "#fff0f0",
                  borderTop: "1px solid #ffc0c0",
                  fontSize: "13px",
                  color: "#c62828",
                }}
              >
                <FaBan size={13} />
                <span>
                  {iBlockedThisUser(activeConversation)
                    ? "You have blocked this user. Unblock to send messages."
                    : "You have been blocked by this user. You cannot send messages."}
                </span>
              </div>
            )}

            {/* Message Input */}
            <div className="message-input-container">
              <input
                type="text"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  handleTyping();
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  currentChatType === "direct" &&
                  isUserBlocked(activeConversation)
                    ? t("userBlocked")
                    : t("typeYourMessage")
                }
                disabled={
                  isSending ||
                  (currentChatType === "direct" &&
                    isUserBlocked(activeConversation))
                }
              />
              <button
                onClick={handleSend}
                disabled={
                  message.trim() === "" ||
                  isSending ||
                  !isConnected ||
                  (currentChatType === "direct" &&
                    isUserBlocked(activeConversation))
                }
                title={
                  !isConnected
                    ? t("notConnected")
                    : currentChatType === "direct" &&
                        isUserBlocked(activeConversation)
                      ? t("userBlocked")
                      : t("sendMessage")
                }
              >
                {isSending ? (
                  <div className="spinner-border small" role="status">
                    <span className="sr-only p-0"></span>
                  </div>
                ) : !isConnected ? (
                  <span style={{ color: "#ffc107" }}>⚡</span>
                ) : currentChatType === "direct" &&
                  isUserBlocked(activeConversation) ? (
                  <span style={{ color: "#dc3545" }}>🚫</span>
                ) : (
                  <FaPaperPlane />
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="no-conversation text-center py-5">
            <FaEnvelope size={64} className="text-muted mb-3" />
            <h4 className="text-muted mb-2">
              {t("no")} {t("conversation")} {t("selected")}
            </h4>

            <p className="text-muted">
              {t("select")} {t("a")} {t("conversation")} {t("to")} {t("start")}{" "}
              {t("messaging")}
            </p>

            {/* Mobile: show button to open contact list */}
            {isMobile && (
              <button
                className="open-contacts-btn"
                onClick={() => setShowSidebar(true)}
              >
                <FaBars style={{ marginRight: 8 }} />
                {t("select")} {t("conversation")}
              </button>
            )}

            <small className="text-muted d-block mt-3">
              {isConnected
                ? `✅ ${t("connectedToServer")}`
                : `❌ ${t("notConnectedToServer")}`}
            </small>
          </div>
        )}
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Create Group Modal */}
      <Modal
        show={showCreateGroupModal}
        onHide={() => setShowCreateGroupModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("create")} {t("new")} {t("group")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("group")} {t("name")} *
              </Form.Label>
              <Form.Control
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder={`${t("enter")} ${t("group")} ${t("name")}`}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("group")} {t("members")} *
              </Form.Label>
              {/* <Form.Control
                as='select'
                multiple
                value={groupMembers}
                onChange={(e) =>
                  setGroupMembers(
                    Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    ),
                  )
                }
                size='5'
                required>
                {availableUsers.map((user) => (
                  <option
                    key={user.id}
                    value={user.id}>
                    {user.name} ({user.role})
                  </option>
                ))}
              </Form.Control> */}

              <ChatUserLisetSelector
                groupMembers={groupMembers}
                change={(_, value) => setGroupMembers(value)}
                availableUsers={availableUsers}
              />
              {/* <Form.Text className='text-muted'>
                Hold Ctrl or Cmd to select multiple members
              </Form.Text> */}
              {groupMembers.length > 0 && (
                <div className="mt-2">
                  <small>
                    {t("selected")}: {groupMembers.length} {t("members")}
                  </small>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCreateGroupModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateGroup}
            disabled={
              !groupName.trim() || groupMembers.length === 0 || isCreatingGroup
            }
          >
            {isCreatingGroup ? (
              <>
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              </>
            ) : (
              <>
                {t("create")} {t("group")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Leave Group Modal */}
      <Modal
        show={showLeaveModal}
        onHide={() => setShowLeaveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("leave")} {t("group")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("leaveGroupConfirm")}</p>
          <small className="text-muted">{t("leaveGroupNote")}</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLeaveModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleLeaveGroup(leaveGroupId)}
          >
            {isLeaveLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              t("leaveGroup")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Group Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("delete")} {t("group")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteGroupConfirm")}</p>
          <small className="text-danger">{t("deleteGroupWarning")}</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDeleteGroup(deleteGroupId)}
          >
            {isDeleteLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>
                {t("delete")} {t("group")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Group Modal */}
      <Modal
        show={showEditGroupModal}
        onHide={() => setShowEditGroupModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("edit")} {t("group")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("group")} {t("name")}
              </Form.Label>
              <Form.Control
                type="text"
                value={editGroupName}
                onChange={(e) => setEditGroupName(e.target.value)}
                placeholder={`${t("enter")} ${t("group")} ${t("name")}`}
              />
            </Form.Group>
          </Form>
          {editGroupError && (
            <div className="alert alert-danger mt-2">{editGroupError}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEditGroupModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleEditGroup}
            disabled={!editGroupName.trim() || editGroupLoading}
          >
            {editGroupLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>
                {t("save")} {t("changes")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Message Modal */}
      <Modal
        show={showDeleteMessageModal}
        onHide={() => setShowDeleteMessageModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("delete")} {t("message")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {deleteForEveryone
              ? t("deleteForEveryoneConfirm")
              : t("deleteForSelfConfirm")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteMessageModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              handleDeleteMessage(messageToDelete, deleteForEveryone)
            }
            disabled={actionLoading}
          >
            {actionLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              t("delete")
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Block User Modal */}
      <Modal
        show={showBlockModal}
        onHide={() => setShowBlockModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("block")} {t("user")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("reason")} ({t("optional")})
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder={`${t("enter")} ${t("reason")} ${t("for")} ${t(
                  "blocking",
                )}...`}
              />
            </Form.Group>
            <p className="text-muted">
              {t("blocking")} {t("thisUser")} {t("will")} {t("prevent")}{" "}
              {t("them")} {t("from")} {t("sending")} {t("you")} {t("messages")}.
            </p>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBlockModal(false)}>
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleBlockUser}
            disabled={blockLoading}
          >
            {blockLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>
                {t("block")} {t("user")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unblock User Modal */}
      <Modal
        show={showUnblockModal}
        onHide={() => setShowUnblockModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("unblock")} {t("user")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("unblockConfirm")}</p>
          <small className="text-muted">{t("unblockInfo")}</small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowUnblockModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="success"
            onClick={handleUnblockUser}
            disabled={unBlockLoading}
          >
            {unBlockLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>
                {t("unblock")} {t("user")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clear Chat History Modal */}
      <Modal
        show={showClearChatModal}
        onHide={() => setShowClearChatModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentChatType === "direct"
              ? t("clearChatHistory")
              : t("clearGroupHistory")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-3">
            <FaInfoCircle className="me-2" />
            <strong>{t("whatThisDoes")}:</strong>

            <ul className="mb-0 mt-2">
              <li>{t("removesMessagesFromYourView")}</li>
              <li>
                {currentChatType === "direct"
                  ? t("otherUserCanStillSee")
                  : t("groupMembersCanStillSee")}
              </li>
              <li>{t("canStillReceiveNewMessages")}</li>
              <li>{t("doesNotDeleteFromServer")}</li>
            </ul>
          </div>

          <p className="mb-0">{t("clearChatHistoryConfirm")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClearChatModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="warning"
            onClick={handleClearChatHistory}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              </>
            ) : (
              <>
                <FaHistory className="me-2" />
                {t("clear")} {t("my")} {t("history")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete All Messages Modal */}
      <Modal
        show={showDeleteAllModal}
        onHide={() => setShowDeleteAllModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("delete")} {t("all")} {t("messages")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteAllMessagesConfirm")}</p>

          <small className="text-danger">
            {t("deleteAllMessagesWarning")}
            {currentChatType === "group" && " " + t("onlyAdminsCanDelete")}
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteAllModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllMessages}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>{t("deleteAllForEveryone")}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mark All as Read Modal */}
      <Modal
        show={showMarkAllReadModal}
        onHide={() => setShowMarkAllReadModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("markAllAsRead")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("confirmMarkAllAsRead")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowMarkAllReadModal(false)}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleMarkAllAsRead}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <div className="spinner-border" role="status">
                <span className="sr-only p-0"></span>
              </div>
            ) : (
              <>{t("markAllRead")}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Chat Settings Modal */}
      <Modal
        show={showChatSettings}
        onHide={() => setShowChatSettings(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("chat")} {t("settings")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6">
              <h5>
                {t("message")} {t("management")}
              </h5>
              <div className="list-group">
                <button
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    setShowChatSettings(false);
                    setShowSelectMessages(true);
                  }}
                >
                  <FaCheckDouble className="me-2" />{" "}
                  {t("selectMultipleMessages")}
                </button>
                <button
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    setShowChatSettings(false);
                    setShowMarkAllReadModal(true);
                  }}
                >
                  <FaEnvelope className="me-2" /> {t("markAllAsRead")}
                </button>
                <button
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    setShowChatSettings(false);
                    setShowClearChatModal(true);
                  }}
                >
                  <FaHistory className="me-2" /> {t("clear")} {t("chat")}{" "}
                  {t("history")}
                </button>
                <button
                  className="list-group-item list-group-item-action text-danger"
                  onClick={() => {
                    setShowChatSettings(false);
                    setShowDeleteAllModal(true);
                  }}
                >
                  <FaTrash className="me-2" /> {t("delete")} {t("all")}{" "}
                  {t("messages")}
                </button>
              </div>
            </div>
            <div className="col-md-6">
              <h5>
                {t("chat")} {t("actions")}
              </h5>
              <div className="list-group">
                {currentChatType === "direct" && (
                  <>
                    <button
                      className="list-group-item list-group-item-action text-danger"
                      onClick={() => {
                        setShowChatSettings(false);
                        setShowBlockModal(true);
                      }}
                    >
                      <FaBan className="me-2" /> {t("block")} {t("user")}
                    </button>
                    <button
                      className="list-group-item list-group-item-action text-danger"
                      onClick={() => {
                        setShowChatSettings(false);
                        handleDeleteConversation();
                      }}
                    >
                      {/* <FaTrashAlt className="me-2" /> Delete Conversation */}
                    </button>
                  </>
                )}
                {currentChatType === "group" && (
                  <>
                    <button
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        setShowChatSettings(false);
                        if (currentConversation) {
                          setSelectedGroupForMembers(currentConversation);
                          setShowGroupMembers(true);
                        }
                      }}
                    >
                      <FaUserFriends className="me-2" /> {t("view")}{" "}
                      {t("members")}
                    </button>
                    <button
                      className="list-group-item list-group-item-action"
                      onClick={() => {
                        setShowChatSettings(false);
                        if (currentChatType === "group" && activeConversation) {
                          fetchAllGroupMessages(activeConversation);
                        }
                      }}
                    >
                      <FaDownload className="me-2" /> {t("loadAllMessages")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowChatSettings(false)}
          >
            {t("close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MessagingModule;
